import PropTypes from 'prop-types'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button, { TYPES as BUTTON_TYPES } from 'components/Button'
import {
  PLAN_SUBSCRIPTION_UNKNOWN,
  getCurrentPlanPrice,
  getNewRatePlanId,
  getPlanSubscriptionType,
} from 'services/plans'
import { selectPlanByPlanId } from 'services/plans/selectors'
import { selectStaticTextByKey } from 'services/static-text/selectors'
import { selectNextProductRatePlanId, selectProductRatePlanId, selectUserAccount } from 'services/user-account/selectors'
import { dismissModal } from 'services/dialog/actions'
import IconV2, { ICON_TYPES } from 'components/Icon.v2'
import { EUR } from 'services/currency'
import HighlightMessage, { MESSAGE_TYPES } from 'components/HighlightMessage'
import { URL_ACCOUNT_PAUSE } from 'services/url/constants'
import { List } from 'immutable'
import { scrollToTop } from 'services/dom'

export const CANCEL_MODAL_TYPE = {
  DEFAULT: 'DEFAULT',
  CANCEL: 'CANCEL',
  PAUSE: 'PAUSE',
}

/**
 * Render the subscription price
 * @returns {JSX.Element} Subscription price
 */
function SubscriptionPrice (props) {
  const { plan, currentPrice = null } = props
  const currencyIso = plan.get('currencyIso')
  const currencySymbol = plan.get('currencySymbol')
  const cost = currentPrice || plan.get('costs', List()).last()

  const [amount, cents] = cost.split('.')
  let price = ''

  if (currencyIso === EUR) {
    price = <><span className="price__cost">{amount}<span className="price__cost-cents">{`,${cents}`}</span></span>{` ${currencySymbol}`}</>
  } else {
    price = <>{currencySymbol}<span className="price__cost">{amount}<span className="price__cost-cents">{`.${cents}`}</span></span></>
  }

  return (
    <span className="price">{price}</span>
  )
}

/**
 * Cancel modal for the price increase
 * @returns {JSX.Element} Cancel modal
 */
function MyAccountCancelModal (props) {
  const { url, cancelModalType } = props
  const userAccount = useSelector(selectUserAccount)
  const staticText = useSelector(selectStaticTextByKey('myAccountCancelModal'))

  let currentRatePlanId = useSelector(selectProductRatePlanId)
  if (getPlanSubscriptionType(currentRatePlanId) === PLAN_SUBSCRIPTION_UNKNOWN) {
    currentRatePlanId = useSelector(selectNextProductRatePlanId)
  }

  const newRatePlanId = getNewRatePlanId(currentRatePlanId)
  const currentPlan = useSelector(selectPlanByPlanId(currentRatePlanId))
  const newPlan = useSelector(selectPlanByPlanId(newRatePlanId))
  const currentPlanPrice = getCurrentPlanPrice(userAccount)
  const isCancelType = cancelModalType === CANCEL_MODAL_TYPE.CANCEL
  const isDefaultType = cancelModalType === CANCEL_MODAL_TYPE.DEFAULT

  const title = isDefaultType ? staticText.get('defaultTitle') : staticText.get('title')
  const subtitle = isDefaultType ? staticText.get('defaultSubtitle') : staticText.get('subtitle')
  const infoContent = isCancelType
    ? <>{staticText.get('info1')}<span>{staticText.get('info2')}</span>{staticText.get('info3')}</>
    : <>{staticText.get('pause1')}<span>{staticText.get('pause2')}</span>{staticText.get('pause3')}<span>{staticText.get('pause4')}</span></>

  const dispatch = useDispatch()

  const handleClose = () => {
    if (!isDefaultType) {
      dispatch(dismissModal())
    }
  }

  return (
    <div className="my-account-cancel-modal">
      <div className="my-account-cancel-modal__title">{title}</div>
      <div className="my-account-cancel-modal__subtitle">{subtitle}</div>
      {
        !isDefaultType && (
          <>
            <div className="my-account-cancel-modal__plans-table">
              <div className="my-account-cancel-modal__plan">
                <div className="my-account-cancel-modal__plan-copy">{staticText.get('currentPrice')}</div>
                <div className="my-account-cancel-modal__plan-cost">
                  <SubscriptionPrice plan={currentPlan} currentPrice={currentPlanPrice} />
                  {` / ${currentPlan.get('billingPeriodAbbreviation')}`}
                </div>
              </div>
              <div className="my-account-cancel-modal__plan my-account-cancel-modal__plan--new">
                <div className="my-account-cancel-modal__plan-copy">{staticText.get('newPrice')}</div>
                <div className="my-account-cancel-modal__plan-cost">
                  <SubscriptionPrice plan={newPlan} />
                  {` / ${currentPlan.get('billingPeriodAbbreviation')}`}
                </div>
              </div>
            </div>
            <HighlightMessage
              content={infoContent}
              className="my-account-cancel-modal__info"
              type={isCancelType ? MESSAGE_TYPES.PINK : MESSAGE_TYPES.TEAL}
            />
          </>
        )
      }
      <div className="my-account-cancel-modal__line"></div>
      <div className="my-account-cancel-modal__buttons">
        <Button
          url={isCancelType ? null : URL_ACCOUNT_PAUSE}
          onClick={isCancelType ? handleClose : scrollToTop}
          type={BUTTON_TYPES.PRIMARY}
          className="my-account-cancel-modal__keep-button"
        >
          {isCancelType ? staticText.get('keepButton') : staticText.get('pauseButton')}
        </Button>
        <Button
          url={url}
          onClick={scrollToTop}
          type={BUTTON_TYPES.SECONDARY}
          className="my-account-cancel-modal__cancel-button"
        >
          <div>
            {staticText.get('cancelButton')}
            <IconV2
              className="my-account-cancel-modal__cancel-button-icon"
              type={ICON_TYPES.CHEVRON_RIGHT}
            />
          </div>
        </Button>
      </div>
    </div>
  )
}

MyAccountCancelModal.defaultProps = {
  cancelModalType: CANCEL_MODAL_TYPE.CANCEL,
}

MyAccountCancelModal.propTypes = {
  cancelModalType: PropTypes.oneOf([
    CANCEL_MODAL_TYPE.DEFAULT,
    CANCEL_MODAL_TYPE.CANCEL,
    CANCEL_MODAL_TYPE.PAUSE,
  ]),
}

export default MyAccountCancelModal
